import { defineStore } from 'pinia'
import { DataGroup, ViewDataGroup, DataGroupType, GroupMemberRole, ItemPermissions, DataGroupMember } from './GroupAdminModel'
import { useGroupAdminApi } from './GroupAdminApi'
import { AsyncData, LoadMode } from '@webapp/util/AsyncData'
import { AsyncDataStore, VersionedKeyList } from '@webapp/util/AsyncDataStore'
import { useUserStore } from './UserStore'
import { useDataGroupMemberStore } from './DataGroupMemberStore'
import { usePermissions, listToItemPermissions } from './Permissions'
import { useTokenManager } from '@webapp/auth/TokenManager'
import { DateTime } from 'luxon'
import { DateTimeUtil } from '@webapp/util/LuxonUtil'
import { assignExisting, isDefined } from '@webapp/util/TypeScriptUtil'
import { PaginatedList, PatchChange } from '@webapp/util/Api'
import { markRaw, reactive } from 'vue'
import { useWorkspaceStore } from '@webapp/rd/WorkspaceStore'
import { uniqBy } from 'lodash'


export const useDataGroupStore = defineStore('dataGroups', () => {
    const asyncDataGroups = new Map<string, AsyncData<DataGroup>>()
    const asyncVisibleDataGroupKeys = new Map<string, AsyncData<VersionedKeyList>>()
    const asyncMemberKeys = new Map<string, AsyncData<VersionedKeyList>>()
    const asyncInviteKeys = new Map<string, AsyncData<VersionedKeyList>>()
    const viewGroups = new Map<string, ViewDataGroup>()
    const asyncPermissions = new Map<string, AsyncData<ItemPermissions>>()

    const gp = useGroupAdminApi()
    const userStore = useUserStore()
    const dataGroupMemberStore = useDataGroupMemberStore()
    const tokenManager = useTokenManager()
    const { getAsyncPermissionsCore, getAsyncPermissionsListCore } = usePermissions()

    function getAsyncGroup(id: string | undefined, loadMode = LoadMode.TrackOnly) {
        return id ? getAsyncGroupList([id], loadMode)[0] : undefined
    }

    function getAsyncGroupList(ids: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        const store = useDataGroupStore() // use to get/return reactive store objects
        const loadAsync = async (ids: string[], requestTime: DateTime) => {
            const plainItems = await gp.getPlainByIdsAsync("datagroups", ids, "owner,workspaceType")
            console.log(`Loaded ${plainItems.length} data groups by id: ${plainItems.map(p => p.id).join(", ")}`)
            return processExpandedDataGroups(plainItems, requestTime)
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncDataGroups, ids, "DataGroup", loadMode, loadAsync)
    }

    async function getGroupListAsync(ids: (string | undefined)[]) {
        return (await Promise.all(getAsyncGroupList(ids, LoadMode.EnsureLoaded).map(a => a.whenLoadCompleted))).filter(isDefined)
    }

    function getLoadedGroup(id: string | undefined) {
        const store = useDataGroupStore() // use to get/return reactive store objects
        return getLoadedGroupList([id]).at(0)
    }

    function getLoadedGroupList(ids: (string | undefined)[]) {
        const store = useDataGroupStore() // use to get/return reactive store objects
        return ids.map(id => store.asyncDataGroups.get(id ?? '')).map(a => a?.data).filter(isDefined)
    }

    function getAsyncKeyListForVisible(loadMode = LoadMode.TrackOnly) {
        const store = useDataGroupStore() // use to get/return reactive store objects
        const query = { pageSize: 1000, expand: "items/owner,items/workspaceType" } // max
        return AsyncDataStore.getAsyncKeyListForRelatedItems("all", "Visible", store.asyncVisibleDataGroupKeys,
            store.asyncDataGroups, "DataGroup", loadMode, (k, t) => getPaginatedListAsync(query, t))!
    }

    function getVisibleGroups(loadMode = LoadMode.TrackOnly) {
        const keyList = getAsyncKeyListForVisible(loadMode)?.data
        return getLoadedGroupList(keyList?.keys ?? [])
    }

    function getOwnedResearchGroups(loadMode = LoadMode.TrackOnly) {
        const dms = dataGroupMemberStore.getMembersForUsers([tokenManager.current.userId], loadMode)
        return getLoadedOwnedResearchGroupsFromMembers(dms)
    }

    async function getOwnedResearchGroupsAsync(loadMode = LoadMode.EnsureLoaded) {
        const dms = await dataGroupMemberStore.getMembersForUsersAsync([tokenManager.current.userId], loadMode)
        return getLoadedOwnedResearchGroupsFromMembers(dms)
    }

    function getLoadedOwnedResearchGroupsFromMembers(dms: DataGroupMember[]) {
        const ownedGroupIds = dms.filter(dm => dm.role == GroupMemberRole.Owner).map(dm => dm.dataGroupId!)
        return getLoadedGroupList(ownedGroupIds)
            .filter(dg => dg.groupType == DataGroupType.Research);
    }

    async function getPaginatedListAsync(query: object, requestTime: DateTime) {
        const plainPage = await gp.getPlainAsync("datagroups", query) as PaginatedList<any>
        if (plainPage.nextPageToken)
            throw 'Too many data groups requested'
        return processExpandedDataGroups(plainPage.items, requestTime)
    }

    async function getViewGroupsForUserAsync(userId?: string, reload = false) {
        userId ??= tokenManager.current.userId
        // only view groups for the current user are cached
        const store = useDataGroupStore() // use to get/return reactive store objects
        if (userId == tokenManager.current.userId && store.viewGroups.size > 0 && !reload) {
            return [...store.viewGroups.values()]
        }

        const plainGroups = await gp.getPlainCollectionAsync(`users/${userId}/viewDataGroups`)
        const groups = plainGroups.map(plainToViewDataGroup)
        if (userId == tokenManager.current.userId) {
            // update cache
            store.viewGroups.clear();
            groups.forEach(g => store.viewGroups.set(g.id!, markRaw(g))) // immutable, so no reactivity needed
        }
        return groups;
    }

    function areGroupsLoaded(ids: (string | undefined)[]) {
        const store = useDataGroupStore()
        return AsyncDataStore.areItemsLoaded(store.asyncDataGroups, ids.filter(isDefined))
    }

    function getAsyncPermissions(id: string | undefined, loadMode = LoadMode.TrackOnly) {
        const store = useDataGroupStore() // use to get/return reactive store objects
        return getAsyncPermissionsCore(store.asyncPermissions, id, "DataGroup", "datagroups", loadMode)
    }

    function getLoadedPermissions(id: string | undefined) {
        return getAsyncPermissions(id)?.data ?? ItemPermissions.None
    }
    
    function getAsyncPermissionsList(ids: string[], loadMode = LoadMode.TrackOnly) {
        const store = useDataGroupStore() // use to get/return reactive store objects
        return getAsyncPermissionsListCore(store.asyncPermissions, ids, "DataGroup", "datagroups", loadMode)
    }

    async function getPermissionsListAsync(ids: string[]) {
        return Promise.all(getAsyncPermissionsList(ids, LoadMode.EnsureLoaded).map(a => a.whenLoadCompleted))
    }
    
    async function addAsync(dataGroup: DataGroup) {
        const store = useDataGroupStore() // use to get/return reactive store objects
        const addCoreAsync = async () => {
            const p = await gp.postPlainAsync(`datagroups`, dataGroup)
            return plainToDataGroup(p, tokenManager.current.userId) // for add, current user is always the owner (and should be loaded)
        }            
        const groupId = await AsyncDataStore.addToStoreMapAsync(store.asyncDataGroups, "DataGroup", dg => dg.id, addCoreAsync)

        AsyncData.invalidate(store.asyncVisibleDataGroupKeys.get('all'))
        AsyncData.invalidate(userStore.asyncDataGroupMemberKeys.get(tokenManager.current.userId ?? ''))
        return groupId
    }

    async function patchAsync(id: string, changes: PatchChange[]) {
        const store = useDataGroupStore() // use to get/return reactive store objects
        const patchCoreAsync = async (id: string, changes: PatchChange[]) => {
            // HACK: patch does not return the owner, but it needs to be present after patching, so ensure it is loaded before
            const old = await getAsyncGroup(id, LoadMode.EnsureLoaded)?.whenLoadCompleted // always loads owner
            const p = await gp.patchPlainAsync(`datagroups/${id}`, changes)
            return plainToDataGroup(p, old?.ownerId)
        }
        await AsyncDataStore.patchFromStoreAsync(store.asyncDataGroups, id, "DataGroup", changes, patchCoreAsync)
    }

    async function unlinkAsync(id: string) {
        await gp.postPlainAsync(`datagroups/${id}/unlink`)
        await getAsyncGroup(id, LoadMode.Reload)?.whenLoadCompleted
    }

    async function deleteAsync(id: string) {
        const store = useDataGroupStore() // use to get/return reactive store objects
        const dg = store.asyncDataGroups.get(id)?.data
        await gp.deleteAsync(`datagroups/${id}`)

        AsyncData.setDeleted(store.asyncDataGroups.get(id))
        AsyncData.setDeleted(store.asyncMemberKeys.get(id))
        AsyncData.invalidate(userStore.asyncDataGroupMemberKeys.get(tokenManager.current.userId!))
        if (dg?.groupType == DataGroupType.Research) {
            const workspaceStore = useWorkspaceStore()
            AsyncData.invalidate(workspaceStore.asyncWorkspaceKeys.get('all'))
        }
        // TODO: clear other stores of cached data belonging to this group?
    }

    async function mergeProfileAsync(fromGroupId: string, toGroupId: string) {
        await gp.postPlainAsync(`datagroups/${fromGroupId}/merge`, undefined, { into: toGroupId })
        // TODO: invalidate stuff
    }

    async function transferAsync(dataGroupId: string, toUserId: string) {
        await gp.postPlainAsync(`datagroups/${dataGroupId}/transfer`, undefined, { to: toUserId })
        // TODO: invalidate stuff or expect client notification
    }

    function addDataGroupsToStore(plainDataGroups: any[], requestTime: DateTime, getLoadedOwnerId?: (p: any) => string) {
        const store = useDataGroupStore() // use to get/return reactive store objects
        const dataGroupMap = processExpandedDataGroups(uniqBy(plainDataGroups, 'id'), requestTime, getLoadedOwnerId)
        AsyncDataStore.addItemMapToStore(store.asyncDataGroups, "DataGroup", dataGroupMap, requestTime)
    }

    function plainToDataGroup(p: any, loadedOwnerId?: string) {
        if (!loadedOwnerId && !p.owner)
            throw `Data group ${p.id} loaded without owner`

        const group = assignExisting(new DataGroup(), p)
        group.groupType = p.groupType ?? DataGroupType.System // default
        group.isChild ??= false
        group.isUserProfile ??= false
        group.createdDate = DateTimeUtil.fromAPI(p.createdDate)
        group.modifiedDate = DateTimeUtil.fromAPI(p.modifiedDate)

        if (loadedOwnerId || p.owner) {
            group.ownerId = loadedOwnerId ?? p.owner.id
        }
        return group
    }

    function processExpandedDataGroups(plainItems: any[], requestTime: DateTime, getLoadedOwnerId?: (p: any) => string) {
        // check length to avoid unnecessary recursion (users can expand data groups and vice versa)
        if (plainItems.length) {
            userStore.addUsersToStore(plainItems.map(dg => dg.owner).filter(isDefined), requestTime)
        }
        return AsyncDataStore.createItemMap(plainItems, "DataGroup", p => plainToDataGroup(p, getLoadedOwnerId?.(p)), dg => dg.id!)
    }

    function plainToViewDataGroup(p: any) {
        const group = assignExisting(new ViewDataGroup(), p)
        group.groupType = p.groupType ?? DataGroupType.System // default
        group.permissions = listToItemPermissions(p.permissions)
        return group
    }
    return {
        asyncDataGroups,
        asyncVisibleDataGroupKeys,
        asyncMemberKeys,
        asyncInviteKeys,
        viewGroups,
        asyncPermissions,
        getAsyncGroup,
        getAsyncGroupList,
        getGroupListAsync,
        getLoadedGroup,
        getLoadedGroupList,
        getAsyncKeyListForVisible,
        getVisibleGroups,
        getOwnedResearchGroups,
        getOwnedResearchGroupsAsync,
        areGroupsLoaded,
        getViewGroupsForUserAsync,
        getAsyncPermissions,
        getLoadedPermissions,
        getAsyncPermissionsList,
        getPermissionsListAsync,
        addAsync,
        patchAsync,
        unlinkAsync,
        deleteAsync,
        mergeProfileAsync,
        transferAsync,
        addDataGroupsToStore,
    }
})
